import { useMemo } from 'react';
import useSWR from 'swr';

import { PackageDefinition } from '#/client/models';
import {
  collectProductDetails,
  fetchAndMapUserProducts,
} from '#/pages/Settings/Products.utils';
import { useAuthStore } from '#/store/auth/auth.store';
import { usePaywallStore } from '#/store/paywall/paywall.store';

export const useFetchUserProducts = (packageDefinition?: PackageDefinition) => {
  const { profileResponse, isProfileLoading } = useAuthStore();
  const { paywallApi: { getUserProducts } = {}, userProductsInvalidatedAt } =
    usePaywallStore();

  const { userProductIds, subscriptionMap, nonWebItems } = useMemo(
    () => collectProductDetails(profileResponse, packageDefinition),
    [profileResponse, packageDefinition]
  );

  // Cache key to trigger revalidation when userProductIds or userProductsInvalidatedAt changes.
  // We update userProductsInvalidatedAt with paywallActions.refreshProducts() to trigger revalidation
  // manually, for instance, when canceling, restoring or changing subscriptions.
  const getUserProductsCacheKey = useMemo(() => {
    return userProductIds.length
      ? `${userProductIds.join(',')}_${userProductsInvalidatedAt}`
      : '';
  }, [userProductsInvalidatedAt, userProductIds]);

  const {
    data: items,
    error: itemsError,
    isLoading: isItemsFetching,
  } = useSWR(
    // Gate on packageDefinition to ensure subscriptionMap (built from it) is
    // ready before fetching. Without this, SWR could cache a result that is
    // missing overridden names/descriptions from the package definition.
    getUserProducts && packageDefinition && getUserProductsCacheKey,
    () =>
      fetchAndMapUserProducts(userProductIds, getUserProducts, subscriptionMap),
    {
      //We need revalidateIfStale here to make sure we don't call the API if data
      //is already in cache. Default behaviour will always fetch on mount even if
      //data is in cache.
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: console.error,
    }
  );

  return {
    items,
    itemsError,
    isItemsFetching,
    nonWebItems,
    profileResponse,
    isProfileLoading,
  };
};
